import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/api";
import { useAuth, FULL_ROLES } from "@/auth";
import {
  ArrowLeft, Users, Vote, MapPin, UserCog, Link2, CheckCircle2, Building2,
} from "lucide-react";

const SUBMODULES = [
  {
    key: "volunteers",
    to: "/dashboard/volunteers",
    icon: Users,
    title: "Padrón de voluntarios",
    desc: "Registro, activación y estatus de cada voluntario asociado a la subcuenta.",
    accent: "from-blue-500 to-indigo-600",
    roles: null, // all
  },
  {
    key: "casillas",
    to: "/dashboard/casillas",
    icon: Vote,
    title: "Gestión de casillas",
    desc: "Catálogo INE con secciones, tipos de casilla y asignación de responsables.",
    accent: "from-emerald-500 to-teal-600",
    roles: null,
  },
  {
    key: "coverage",
    to: "/dashboard/coverage",
    icon: MapPin,
    title: "Cobertura",
    desc: "Mapa de despliegue territorial · huecos operativos y densidad por sección.",
    accent: "from-amber-500 to-orange-600",
    roles: null,
  },
  {
    key: "users",
    to: "/dashboard/users",
    icon: UserCog,
    title: "Usuarios y roles",
    desc: "Coordinadores, permisos por nivel y jerarquía operativa de la campaña.",
    accent: "from-purple-500 to-fuchsia-600",
    roles: "full",
  },
  {
    key: "links",
    to: "/dashboard/links",
    icon: Link2,
    title: "Enlaces de reclutamiento",
    desc: "Links públicos con lector OCR de INE para captación segura de voluntarios.",
    accent: "from-cyan-500 to-sky-600",
    roles: "full",
    badge: "OCR INE",
  },
  {
    key: "approvals",
    to: "/dashboard/links/approvals",
    icon: CheckCircle2,
    title: "Aprobaciones pendientes",
    desc: "Revisión de altas provenientes de enlaces de reclutamiento antes de activar.",
    accent: "from-rose-500 to-red-600",
    roles: "full",
  },
];

export default function ModuloEstructura() {
  const { user } = useAuth();
  const isFull = FULL_ROLES.includes(user?.role);
  const items = SUBMODULES.filter((s) => s.roles !== "full" || isFull);
  const [pendingCount, setPendingCount] = useState(null);

  useEffect(() => {
    if (!isFull) return;
    api.get("/links", { params: { status: "pending" } })
      .then((r) => setPendingCount(r.data.length))
      .catch(() => setPendingCount(null));
  }, [isFull]);

  const badgeFor = (key) => {
    if (key === "links" && pendingCount > 0) {
      return { label: `${pendingCount} pendiente${pendingCount === 1 ? "" : "s"}`, tone: "red" };
    }
    return null;
  };

  return (
    <div className="p-6 space-y-6 max-w-6xl" data-testid="modulo-estructura">
      <div className="flex items-center gap-3 text-sm text-slate-500">
        <Link to="/dashboard" className="hover:text-slate-800 flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Dashboard
        </Link>
        <span>·</span>
        <span className="text-slate-700 font-medium">Módulo 2 · Construcción de estructura</span>
      </div>

      <div className="flex items-start gap-4">
        <div className="w-12 h-12 grid place-items-center rounded-lg bg-gradient-to-br from-slate-700 to-slate-900 shrink-0">
          <Building2 className="w-6 h-6 text-white" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Módulo 2</div>
          <h1 className="font-display text-3xl font-black text-slate-900">Construcción de estructura</h1>
          <p className="text-slate-600 text-sm mt-1 max-w-2xl">
            Diseña y despliega la red operativa de tu campaña: voluntarios, casillas, coordinadores,
            enlaces de reclutamiento con lectura OCR de INE y aprobaciones de altas.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((m) => {
          const Icon = m.icon;
          const dynamicBadge = badgeFor(m.key);
          const badge = dynamicBadge || (m.badge ? { label: m.badge, tone: "blue" } : null);
          return (
            <Link
              key={m.key}
              to={m.to}
              className="group relative rounded-lg border border-slate-200 bg-white p-5 hover:border-blue-500 hover:shadow-md transition-all overflow-hidden"
              data-testid={`estructura-card-${m.key}`}
            >
              <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${m.accent}`} />
              <div className="flex items-start justify-between gap-3">
                <div className={`w-10 h-10 grid place-items-center rounded-md bg-gradient-to-br ${m.accent} shrink-0`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                {badge && (
                  <span
                    className={`text-[9px] tracking-widest uppercase font-bold px-2 py-1 rounded ${
                      badge.tone === "red"
                        ? "text-white bg-red-600 animate-pulse"
                        : "text-blue-700 bg-blue-100"
                    }`}
                    data-testid={`estructura-badge-${m.key}`}
                  >
                    {badge.label}
                  </span>
                )}
              </div>
              <div className="mt-4 font-display font-bold text-base text-slate-900 group-hover:text-blue-700 transition-colors">
                {m.title}
              </div>
              <div className="text-sm text-slate-500 mt-1 leading-snug">{m.desc}</div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
